import React, { useEffect, useState } from "react";
import { useParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useWindowSize } from "../../../../../Helpers";
import Button from "@components/Button";
import "./style.scss";
import LinearGradientProgressBar from "@components/LinearGradientProgressBar";
import StickyButton from "../../../../../components/StickyButton";
import PagesHeader from "@components/PagesHeader";
import StickyContent from "@components/StickyContent";
import BrandLogo from "@components/BrandLogo";
import { useTransition } from "@react-spring/web";
import CorrectAnswerPopup from "@components/CorrectAnswerPopup/CorrectAnswerPopup";
import DOMPurify from "dompurify";

export default function Find({
  currentQuestion,
  setCurrentQuestion,
  setAnswers,
  answers,
  sendAnswers,
}) {
  const { ident } = useParams();
  const { width } = useWindowSize();
  const queryClient = useQueryClient();
  const gameQuestionsQuery = queryClient.getQueryData([
    "game-questions",
    ident,
  ]);
  const XGA = width >= 1020;
  const [selected, setSelected] = useState(0);
  const [tempAnswers, setTempAnswers] = useState([]);
  const [selectedAnswerCopy, setSelectedAnswerCopy] = useState("");
  const [selectedAnswerCopyTitle, setSelectedAnswerCopyTitle] = useState("");
  const [selectedAnswerCopySub, setSelectedAnswerCopySub] = useState("");
  const [popupOpen, setPopupOpen] = useState(false);
  const [, setPopup] = useState(null);

  const isUltraInsider =
    location.pathname.includes("/ultra-insider") ||
    location.pathname.includes("/ultrainsider");

  const question = gameQuestionsQuery?.content?.[currentQuestion];
  const showCopyOnClick = Boolean(question?.showCopyOnClick);
  const buttonLabel = question?.buttonCopy || "Nächste";

  const [pinSources, setPinSources] = useState(
    gameQuestionsQuery?.content[currentQuestion]?.answers.map(
      () => gameQuestionsQuery.pin,
    ),
  );

  const handleAddPath = (index) => {
    let ident =
      gameQuestionsQuery.content[currentQuestion]?.answers[index].ident;
    let newTemp = tempAnswers;
    setSelectedAnswerCopy(
      gameQuestionsQuery.content[currentQuestion]?.answers[index].copy,
    );
    setSelectedAnswerCopyTitle(
      gameQuestionsQuery.content[currentQuestion]?.answers[index].copyTitle,
    );
    setSelectedAnswerCopySub(
      gameQuestionsQuery.content[currentQuestion]?.answers[index].copySub,
    );
    if (newTemp.includes(ident)) return;
    newTemp.push(ident);

    const updatedSources = [...pinSources];

    updatedSources[index] = gameQuestionsQuery.clickedPin;
    setPinSources(updatedSources);

    setTempAnswers([...newTemp]);
    setSelected(1);
  };

  const openPopup = () => {
    if (popupOpen) {
      setPopupOpen(false);
      setTimeout(() => {
        setPopupOpen(true);
      }, 500);
    } else {
      setPopupOpen(true);
    }
  };

  const transitionPopup = useTransition(popupOpen, {
    from: {
      opacity: 0,
      scale: 0,
    },
    enter: {
      opacity: 1,
      scale: 1,
    },
    leave: {
      opacity: 0,
      scale: 0,
    },
    config: {
      duration: 200,
    },
  });

  useEffect(() => {
    if (popupOpen) {
      const timeout = setTimeout(() => {
        setPopupOpen(false);
      }, 10000);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [popupOpen]);

  const handleClosePopup = () => {
    setPopupOpen(false);
  };

  const hadnleButtonClick = () => {
    nextQuestion();
  };

  function nextQuestion() {
    setAnswers((prev) => [
      ...prev,
      {
        question_ident: `q${currentQuestion}`,
        answer_ident: gameQuestionsQuery.content[currentQuestion].setAnswer,
      },
    ]);
    setCurrentQuestion(currentQuestion + 1);
  }

  return (
    <section
      className={`game-page meet-mechanics ${ident} ${
        isUltraInsider ? "ultra-insider" : ""
      }`}
    >
      <div>
        <PagesHeader back>
          <p
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(gameQuestionsQuery?.title),
            }}
          ></p>
        </PagesHeader>
        <div className="main-game-wrppaer">
          <div className="desktop-wrapper">
            <LinearGradientProgressBar
              value={
                tempAnswers.length /
                gameQuestionsQuery.content[currentQuestion]?.answers.length
              }
            />
            <div className="game-question">
              {gameQuestionsQuery.content[currentQuestion].header && (
                <p
                  className="question-header"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(gameQuestionsQuery.content[currentQuestion].header),
                  }}
                />
              )}
              <div className="game-question">
                <h2
                  className="question"
                  dangerouslySetInnerHTML={{
                    __html:
                      DOMPurify.sanitize(gameQuestionsQuery.content[currentQuestion].question),
                  }}
                />
              </div>
            </div>
          </div>

          <div className="side-img">
            <div
              className="desktop-background desktop-path"
              style={{
                backgroundImage: `url(${
                  width < XGA ? question?.mobileImg : question?.desktopImg
                })`,
              }}
            >
              {question?.answers.map((element, index) => {
                const clip =
                  width < XGA ? element.clipPathMobile : element.clipPathDesktop;

                return (
                  <div
                    style={{
                      position: "absolute",
                      top: clip.top,
                      left: clip.left,
                    }}
                    className={`pin-svg ${showCopyOnClick ? "pin-center" : ""} ${
                      tempAnswers.includes(element.ident) ? "selected" : ""
                    }`}
                    key={element.ident}
                    onClick={() => {
                      handleAddPath(index);
                      if (showCopyOnClick) openPopup();
                    }}
                  ></div>
                );
              })}
              {showCopyOnClick &&
                transitionPopup((style, item) =>
                  item ? (
                    <CorrectAnswerPopup
                      style="find-copy"
                      attributes={{ popper: {} }}
                      setPopup={setPopup}
                      handleClosePopup={handleClosePopup}
                      styles={{
                        position: "absolute",
                        zIndex: 300,
                        ...style,
                      }}
                      copyTitle={selectedAnswerCopyTitle}
                      copy={selectedAnswerCopy}
                      CopySub={selectedAnswerCopySub}
                    />
                  ) : null,
                )}
            </div>
            {XGA && (
              <StickyContent>
                <div className="desktop-buttons">
                  <Button
                    disabled={
                      tempAnswers.length !==
                      gameQuestionsQuery.content[currentQuestion]?.answers
                        .length
                    }
                    action={hadnleButtonClick}
                  >
                    {buttonLabel}
                  </Button>
                </div>
              </StickyContent>
            )}
          </div>
        </div>
      </div>

      {!XGA && (
        <StickyButton
          disabled={
            tempAnswers.length !==
            gameQuestionsQuery.content[currentQuestion]?.answers.length
          }
          action={hadnleButtonClick}
        >
          {buttonLabel}
        </StickyButton>
      )}
    </section>
  );
}
